<template>
  <div>
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <div class="row align-items-center">
              <div class="col-md-12">
                <div class="form-group">
                  <label>Microsoft Quotation file</label>
                  <div class="row">
                    <div class="col-md-5">
                      <div class="input-group">
                        <input type="file" class="form-control" @change="fileSelected" />
                        <div class="input-group-append">
                          <button class="btn btn-info" @click="uploadFile()" :disabled="loadingQuotations">Upload File</button>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-5"></div>
                    <div class="col-md-2">
                      <button class="btn btn-primary float-right" @click="publish()" :disabled="loadingQuotations">Publish</button>
                    </div>
                  </div>

                  <div class="row mt-3">
                    <div class="col-md-5">
                      <div class="input-group">
                        <input type="text" ref="searchBox" class="form-control" placeholder="Search Offers" @keyup.enter="applySearch">
                        <div class="input-group-append">
                          <button class="btn btn-secondary" @click="applySearch" :disabled="loadingQuotations"><i class="fa-solid fa-magnifying-glass"></i></button>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4"></div>
                    <div class="col-md-3">
                      <div class="form-inline float-right">
                          <label class="my-0">$ 1.00 = R {{ this.microsoftROE }}</label>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div class="row-spacing table-responsive table-bordered table-height">
                    <table class="table text-nowrap">
                      <thead>
                        <tr>
                          <th></th>
                          <th class="offer-col">Offer</th>
                          <th>Term</th>
                          <th>Billing Plan</th>
                          <th class="text-center">[$] Price (excl. VAT)</th>
                          <th class="text-center">[R] Price (excl. VAT)</th>
                        </tr>
                      </thead>
                      <tbody v-if="filteredQuotationsData.length > 0 && !loadingQuotations">
                        <tr v-for="q in filteredQuotationsData" :key="rowKey(q)" :class="q.rowClass">
                          <td class="text-center">
                            <input
                              type="checkbox"
                              :checked="isSelected(rowKey(q))"
                              @change="toggleRow(rowKey(q), $event)"
                            />
                          </td>
                          <td class="offer-col">{{ q.label }}</td>
                          <td class="text-center"><span :class="getQuotationColorTerm(q.termDuration)">{{ q.termDuration }}</span></td>
                          <td class="text-center"><span :class="getQuotationColorBilling(q.billingPlan)">{{ q.billingPlan }}</span></td>
                          <td :class="q.cellClass">{{ q.formattedRrp }}</td>
                          <td :class="q.cellClass">{{ q.formattedRand }}</td>
                        </tr>
                      </tbody>
                      <tbody v-else-if="filteredQuotationsData.length === 0 && !loadingQuotations">
                        <tr>
                          <td colspan="6">No Quotations to Display</td>
                        </tr>
                      </tbody>
                      <tbody v-else>
                        <tr>
                          <td colspan="6">
                            <DataLoader :loadingText="tableMessage" />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import DataLoader from '@/components/GeneralComponents/DataLoader.vue';
  import { getProduct } from "@/api/v1/products.api";

  export default {
    components: {
      DataLoader
    },
    data() {
      return {
        productNumber: 776,
        microsoftProduct: {},
        microsoftROE: 0.00,

        quotationFile: null,
        fileName: '',

        quotations: [],
        prevQuotations: [],

        fileUploaded: false,
        selectedRows: new Set(),
        search: "",
        filteredQuotationsData: [],
        loadingQuotations: false,
        tableMessage: "No Response from Server"
      }
    },
    created() {
      this.$root.$emit('SET_PAGE_TITLE', this.$route.meta.title);
    },
    mounted() {
      this.getExchangeRate();
      this.getQuotations();
      this.getPublishedPackages();
    },
    methods: {
      fileSelected(event) {
        this.quotationFile = event.target.files[0];
        this.fileName = this.quotationFile.name;
      },
      getQuotations() {
        this.loadingQuotations = true;
        this.tableMessage = "Fetching Quotations...";
        this.prevQuotations = this.deepCopyObject(this.quotations);
        this.$http.get(this.$config.aimsAPIv3 + "quotation/microsoft/all")
          .then(response => {
            this.tableMessage = "Processing Quotations...";
            this.quotations = response.data;
            this.filteredQuotationsData = this.formatQuotationList(this.quotations);
          }, error => {
            console.error(error);
            this.showError("Error fetching Microsoft Quotations", error);
          })
          .finally(() => {
            this.loadingQuotations = false;
            this.tableMessage = "No Response from Server"
          });
      },
      getPublishedPackages() {
        this.$http.get(this.$config.aimsAPIv3 + "quotation/microsoft/published")
        .then(response => {
          this.selectedRows = new Set(
            response.data.map(pkg => {
              const termDuration = (() => {
                switch(pkg.contractTerm) {
                  case 'Month to month': return 'P1M';
                  case '1 Year': return 'P1Y';
                  case '2 Year': return 'P2Y';
                  case '3 Year': return 'P3Y';
                  case '4 Year': return 'P4Y';
                  default: return '';
                }
              })();

              const billingPlan = (() => {
                switch(pkg.billingFrequency) {
                  case 'Monthly': return 'MONTHLY';
                  case 'Yearly': return 'YEARLY';
                  case 'Triennial': return 'TRIENNIAL';
                  case 'OneTime': return 'ONCE_OFF';
                  default: return '';
                }
              })();

              if (termDuration.length > 0) {
                return `${pkg.externalId}-${pkg.segment}-${termDuration}-${billingPlan}`;
              } else {
                return `${pkg.externalId}-${pkg.segment}-N/A-${billingPlan}`;
              }
            })
          )
        }, error => {
          console.error(error);
          this.showError("Error fetching Microsoft Quotations", error);
        })
      },
      uploadFile() {
        this.loadingQuotations = true;
        this.tableMessage = "Uploading Quotations..."

        const formData = new FormData();
        formData.append('file', this.quotationFile);

        this.$http.post(this.$config.aimsAPIv3 + "quotation/microsoft/upload", formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }).then(response => {
          this.tableMessage = "Processing Quotations..."
          this.fileUploaded = true;
          this.prevQuotations = this.deepCopyObject(this.quotations);
          this.quotations = response.data;
          this.filteredQuotationsData = this.formatQuotationList(this.quotations);
          this.showSuccess("Updated Microsoft Quotations");
        }, error => {
          console.error(error);
          this.showError("Error Processing Microsoft Quotation File", error);
        }).finally(() => {
          this.loadingQuotations = false;
          this.tableMessage = "No Response from Server"
          this.publish();
        });
      },
      getExchangeRate() {
          getProduct(this.productNumber).then((success) => {
              this.microsoftProduct = success.data;
              this.microsoftROE = this.microsoftProduct.productvariables.find(v => v.name === "microsoft-exchange-rate").value;
          });  
      },
      formatQuotationList(list) {
        const formatted = [];
        for (let i = 0; i < list.length; i++) {
          const q = list[i];
          const prev = formatted[i - 1];
          const rowClass = this.computeRowClass(q, prev);
          const comparison = this.compareWithPrev(q, 'rrp');
          const cellClass = comparison === 'increased'
            ? 'price-up' : comparison === 'decreased'
            ? 'price-down' : 'price-same';

          formatted.push({
            ...q, rowClass, cellClass,
            formattedRrp: this.decimalFormat(q.rrp),
            formattedRand: this.decimalFormat(q.rrp, 'Rand'),
          });
        }
        return formatted;
      },
      publish() {
         this.$swal({
                title: "Publish all selected prices.",
                text: "HALO API has a rate limit. Please wait a few moments after Publish.",
                type: "warning",
                showCancelButton: true,
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes",
            }).then((result) => {
                if (result.value) {
                  if (this.selectedRows.size > 0) {
                    this.$http.post(this.$config.aimsAPIv3 + "quotation/microsoft/publish", Array.from(this.selectedRows))
                    .then(response => {
                      this.showSuccess(response.data.message);
                    }, error => {
                      console.error(error);
                      this.showError("Error Publishing Quotations", error);
                    }).finally(() => {
                      this.getPublishedPackages();
                    });
                  }
                }
            });       
      },
      doSearch() {
        this.loadingQuotations = true;
        this.tableMessage = "Searching Quotations..."

        const s = this.search.trim().toLowerCase();
        const filtered = this.quotations.filter(q =>
          !s || q.label.toLowerCase().includes(s)
        );
        
        this.filteredQuotationsData = this.formatQuotationList(filtered);

        this.loadingQuotations = false;
        this.tableMessage = "No Response from Server"
      },
      applySearch() {
        this.search = this.$refs.searchBox.value.trim();
        this.doSearch();
      },
      decimalFormat(value, currency) {
        if (value > 0) {
          if (currency == "Rand") { return "R " + parseFloat(value * this.microsoftROE).toFixed(2); } 
          else { return "$ " + parseFloat(value).toFixed(2); }
        } else { return "-"; }
      },
      compareWithPrev(quotation, field) {
        const prev = this.prevQuotations.find(
          q => q.externalId === quotation.externalId &&
          q.termDuration === quotation.termDuration &&
          q.billingPlan === quotation.billingPlan &&
          q.segment === quotation.segment);

        if (!prev) return null;

        var newVal = parseFloat(quotation[field]);
        var oldVal = parseFloat(prev[field]);

        if (quotation.billingPlan == "Monthly" && quotation.termDuration == "P1Y") {
          newVal = parseFloat(quotation[field]/12);
        }

        if (newVal > oldVal) return "increased";
        if (newVal < oldVal) return "decreased";
        return "same";
      },
      getQuotationColorTerm(attribute) {
        if (attribute == "P1M") { return "badge badge-m" } else 
        if (attribute == "P1Y") { return "badge badge-1y" } else 
        if (attribute == "P3Y") { return "badge badge-3y" } else { return "badge badge-info" }
      },
      getQuotationColorBilling(attribute) {
        if (attribute == "MONTHLY") { return "badge badge-m" } else 
        if (attribute == "YEARLY") { return "badge badge-1y" } else 
        if (attribute == "TRIENNIAL") { return "badge badge-3y" } else { return "badge badge-info" }
      },
      // computeRowClass(q, prev) { //there was two methods with same name, logic says that the first one is overwritten, i hope.
      //   if (!prev) return 'row-light';
      //   if (q.externalId === prev.externalId) return prev.rowClass;
      //   return prev.rowClass === 'row-light' ? 'row-dark' : 'row-light';
      // },
      computeRowClass(q, prev) {
        if (!prev) return 'row-light';

        const a = (q.externalId || '').toLowerCase();
        const b = (prev.externalId || '').toLowerCase();

        if (a && b && a === b) return prev.rowClass || 'row-light';
        return prev.rowClass === 'row-light' ? 'row-dark' : 'row-light';
      },
      rowKey(q) {
        if (q.termDuration.length > 0) {
          return `${q.externalId}-${q.segment}-${q.termDuration}-${q.billingPlan}`;
        } else {
          return `${q.externalId}-${q.segment}-N/A-${q.billingPlan}`;
        }
      },
      toggleRow(id, event) {
        if (event.target.checked) {
          this.selectedRows.add(id);
        } else {
          this.selectedRows.delete(id);
        }
      },
      isSelected(id) {
        return this.selectedRows.has(id);
      },
      clearSelections() {
        this.selectedRows.clear();
      }
    },
  }

</script>

<style>
  .customBtn {
    height: 25px;
    width: 25px;
    text-align: center;
    padding: 6px 0;
    font-size: 13.5px;
    line-height: 0.885;
    border-radius: 50%;
    margin-top: 3px;
  }

td {
  font-size: 15px !important;
}

th.offer-col,
td.offer-col {
  width: 600px;
  max-width: 600px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.price-up {
  color: #10aa10 !important;
  font-weight: bold;
}

.price-down {
  color: #c01616 !important;
  font-weight: bold;
}

.badge-m {
  background-color: #ee0909 !important;
  color: #fff;
}

.badge-1y {
  background-color: #0088ff !important;
  color: #fff;
}

.badge-3y {
  background-color: #20ae52 !important;
  color: #fff;
}

.row-light {
  background-color: #f0f0f0;
}

.row-dark {
  background-color: #f9f9f9;
}

.pb-card {
  margin-bottom: 5em;
}

.table-height {
  max-height: 540px;
}

.pull-right {
  float: right;
}
</style>
